import type { GoogleChatSpaceType } from './googleChat';
import { KnowledgeOwnerType } from './knowledge';
import type { MessagingPlatform } from './messagingPlatform.enum';
import type { PrepareTrigger } from './prepareDoBoxPlan';
import type { TeamsChatType } from './teamsCache';
import type {
  CalendarAddAttendeesStepConfig,
  CalendarCreateEventStepConfig,
  CalendarDeleteEventStepConfig,
  CalendarRemoveAttendeesStepConfig,
  CalendarRescheduleEventStepConfig,
  CalendarRsvpStepConfig,
  LoadMeetingsStepConfig,
} from './userCommunicationAgent/calendar';
import type { LoadChatCommunicationsStepConfig } from './userCommunicationAgent/chatCommunications';
import type {
  AcceptDoBoxSuggestionStepConfig,
  AddDoBoxCommentStepConfig,
  AppendToDoBoxStepConfig,
  BaseDoboxTriggerConfig,
  CheckAndAppendDoBoxStepConfig,
  CommentAddedConfig,
  CreateDoBoxStepConfig,
  DoBoxEventQueueBody,
  GenerateDoBoxStepConfig,
  LoadDoBoxStepConfig,
  ProcessDoBoxStepConfig,
  ResolveDoBoxStepConfig,
  UpdateDoBoxLabelsStepConfig,
  UpdateDoBoxParticipantsStepConfig,
  ValidateDoBoxRulesStepConfig,
} from './userCommunicationAgent/dobox';
import { ValidateDoBoxRulesInputMode } from './userCommunicationAgent/dobox';
import type {
  AllEmailsTriggerConfig,
  CategoryMatchTriggerConfig,
  ChangeCategoryStepConfig,
  DomainMatchTriggerConfig,
  DraftEmailStepConfig,
  DraftReplyStepConfig,
  EmailMatchTriggerConfig,
  LabelMatchTriggerConfig,
  LabelStepConfig,
  LoadCommunicationsStepConfig,
  PrimaryInboxTriggerConfig,
  RecipientsStepConfig,
  SendEmailStepConfig,
  SendNotificationEmailStepConfig,
} from './userCommunicationAgent/email';
import type {
  FacebookNewCommentTriggerConfig,
  FacebookNewMessageTriggerConfig,
  FacebookPublishPostStepConfig,
  FacebookReplyCommentStepConfig,
  LoadFacebookCommentsStepConfig,
  LoadFacebookPostsStepConfig,
  SendFacebookMessageStepConfig,
} from './userCommunicationAgent/facebook';
import type { GitHubEventMatchTriggerConfig } from './userCommunicationAgent/github';
import type { GbpReplyReviewStepConfig } from './userCommunicationAgent/googleBusiness';
import type {
  GoogleChatAuthorMatchTriggerConfig,
  GoogleChatNewMessageTriggerConfig,
  GoogleChatSpaceMatchTriggerConfig,
  SendGoogleChatMessageStepConfig,
} from './userCommunicationAgent/googleChat';
import type {
  InstagramNewMessageTriggerConfig,
  InstagramPostCommentTriggerConfig,
  PublishInstagramPostStepConfig,
  ReplyInstagramCommentStepConfig,
  SendInstagramMessageStepConfig,
} from './userCommunicationAgent/instagram';
import type {
  KnowledgePageTriggerConfig,
  LoadKnowledgePageStepConfig,
} from './userCommunicationAgent/knowledge';
import type {
  CreatePersonEventStepConfig,
  LoadTeamPeopleStepConfig,
  PersonCreatedTriggerConfig,
  PersonEventQueueBody,
  SearchPeopleStepConfig,
  UpdatePersonNotesStepConfig,
} from './userCommunicationAgent/people';
import type { SyntheticCommunicationInput } from './userCommunicationAgent/scenarioSimulation';
import type {
  SendSlackMessageStepConfig,
  SlackAuthorMatchTriggerConfig,
  SlackChannelMatchTriggerConfig,
  SlackNewMessageTriggerConfig,
} from './userCommunicationAgent/slack';
import type {
  CheckTeamCalendarStepConfig,
  LoadTeamMembersStepConfig,
} from './userCommunicationAgent/team';
import type {
  SendTeamsMessageStepConfig,
  TeamsNewMessageTriggerConfig,
} from './userCommunicationAgent/teams';
import type {
  SendWhatsAppMessageStepConfig,
  WhatsAppNewMessageTriggerConfig,
} from './userCommunicationAgent/whatsapp';

export enum AgentKeyPart {
  AGENT = 'AGENT',
  TRIGGER = 'TRIGGER',
  STEP = 'STEP',
  METADATA = 'METADATA',
  USER = 'USER',
  TYPE = 'TYPE',
  ID = 'ID',
  CONVERSATION = 'CONVERSATION',
}

export enum TriggerType {
  EMAIL = 'EMAIL',
  SLACK = 'SLACK',
  GOOGLE_CHAT = 'GOOGLE_CHAT',
  INSTAGRAM = 'INSTAGRAM',
  INSTAGRAM_CONTENT = 'INSTAGRAM_CONTENT',
  SCHEDULE = 'SCHEDULE',
  DOBOX = 'DOBOX',
  MANUAL = 'MANUAL',
  WHATSAPP = 'WHATSAPP',
  KNOWLEDGE = 'KNOWLEDGE',
  GITHUB = 'GITHUB',
  PEOPLE = 'PEOPLE',
  GOOGLE_BUSINESS = 'GOOGLE_BUSINESS',
  // FACEBOOK is Page comments; FACEBOOK_MESSENGER is Page direct messages.
  FACEBOOK = 'FACEBOOK',
  FACEBOOK_MESSENGER = 'FACEBOOK_MESSENGER',
  TEAMS = 'TEAMS',
}

/**
 * The platforms that have a built-in system agent: EMAIL is seeded at signup
 * (createDefaultEmailAgent), while SLACK, GOOGLE_CHAT and TEAMS are each
 * reconciled by their own StreamReceiver when the user's first or last
 * credential is connected or disconnected — Slack on `SlackUserCredentials`
 * (INSERT activates, REMOVE deactivates), Google Chat on
 * `Google/StreamReceiver`'s `chatSubscriptionId` transition (absent→present on
 * MODIFY activates; the last chat-enabled account removed on REMOVE
 * deactivates). TEAMS is reconciled off `MicrosoftSyncDataV2`, whose row is
 * keyed by `homeAccountId` and can carry Outlook, Teams, or both — so unlike
 * Slack or Google Chat it has two distinct connection edges (INSERT when the
 * Microsoft account is new, MODIFY when that account already existed for
 * Outlook and gains Teams) and two distinct disconnection edges (MODIFY when
 * Outlook remains on the same account after Teams is dropped, REMOVE when
 * nothing is left on the account). Narrower than TriggerType on purpose —
 * toggleSystemAgent only ever needs to resolve one of these platforms, and
 * accepting the full TriggerType enum there would let a caller ask for a
 * "system agent" on a platform that has none.
 */
export enum SystemAgentPlatform {
  EMAIL = 'EMAIL',
  SLACK = 'SLACK',
  GOOGLE_CHAT = 'GOOGLE_CHAT',
  TEAMS = 'TEAMS',
}

export const SYSTEM_AGENT_PLATFORM_TRIGGER_TYPE: Record<
  SystemAgentPlatform,
  TriggerType
> = {
  [SystemAgentPlatform.EMAIL]: TriggerType.EMAIL,
  [SystemAgentPlatform.SLACK]: TriggerType.SLACK,
  [SystemAgentPlatform.GOOGLE_CHAT]: TriggerType.GOOGLE_CHAT,
  [SystemAgentPlatform.TEAMS]: TriggerType.TEAMS,
};

export enum TriggerKey {
  // EMAIL
  ALL_EMAILS = 'ALL_EMAILS',
  PRIMARY_INBOX = 'PRIMARY_INBOX',
  LABEL_MATCH = 'LABEL_MATCH',
  CATEGORY_MATCH = 'CATEGORY_MATCH',
  EMAIL_MATCH = 'EMAIL_MATCH',
  DOMAIN_MATCH = 'DOMAIN_MATCH',
  SENT_EMAILS = 'SENT_EMAILS',

  // SLACK
  SLACK_NEW_MESSAGE = 'SLACK_NEW_MESSAGE',
  SLACK_CHANNEL_MATCH = 'SLACK_CHANNEL_MATCH',
  SLACK_AUTHOR_MATCH = 'SLACK_AUTHOR_MATCH',

  // GOOGLE CHAT
  GOOGLE_CHAT_NEW_MESSAGE = 'GOOGLE_CHAT_NEW_MESSAGE',
  GOOGLE_CHAT_SPACE_MATCH = 'GOOGLE_CHAT_SPACE_MATCH',
  GOOGLE_CHAT_AUTHOR_MATCH = 'GOOGLE_CHAT_AUTHOR_MATCH',

  // INSTAGRAM
  INSTAGRAM_NEW_MESSAGE = 'INSTAGRAM_NEW_MESSAGE',
  INSTAGRAM_POST_COMMENT = 'INSTAGRAM_POST_COMMENT',

  // WHATSAPP
  WHATSAPP_NEW_MESSAGE = 'WHATSAPP_NEW_MESSAGE',

  // CRON
  CRON_SCHEDULE = 'CRON_SCHEDULE',

  // DOBOX
  DOBOX_CREATED = 'DOBOX_CREATED',
  PARTICIPANT_ADDED = 'PARTICIPANT_ADDED',
  LABEL_ADDED = 'LABEL_ADDED',
  LABEL_REMOVED = 'LABEL_REMOVED',
  TASK_GROUP_CHANGED = 'TASK_GROUP_CHANGED',
  COMMENT_ADDED = 'COMMENT_ADDED',
  DOBOX_RESOLVED = 'DOBOX_RESOLVED',
  DOBOX_REOPENED = 'DOBOX_REOPENED',
  DOBOX_APPROVED = 'DOBOX_APPROVED',
  DOBOX_REJECTED = 'DOBOX_REJECTED',

  // MANUAL
  MANUAL_RUN = 'MANUAL_RUN',

  // KNOWLEDGE
  KNOWLEDGE_PAGE_CREATED = 'KNOWLEDGE_PAGE_CREATED',

  // GitHub
  GITHUB_EVENT_MATCH = 'GITHUB_EVENT_MATCH',

  // PEOPLE
  PERSON_CREATED = 'PERSON_CREATED',
  // Fires when a visitor registers as a CRM contact via a published landing-page
  // artifact (create_crm_contact). Cataloged here; fired by a separate change
  // in the People stream receiver (Lambda scope).
  LANDING_PAGE_LEAD_CREATED = 'LANDING_PAGE_LEAD_CREATED',

  // GOOGLE BUSINESS PROFILE
  GBP_NEW_REVIEW = 'GBP_NEW_REVIEW',
  GBP_UPDATED_REVIEW = 'GBP_UPDATED_REVIEW',

  // FACEBOOK
  FACEBOOK_NEW_COMMENT = 'FACEBOOK_NEW_COMMENT',
  FACEBOOK_NEW_MESSAGE = 'FACEBOOK_NEW_MESSAGE',

  // TEAMS
  TEAMS_NEW_MESSAGE = 'TEAMS_NEW_MESSAGE',
}

export enum ContentFilterType {
  KEYWORD = 'KEYWORD',
  REGEX = 'REGEX',
}

export enum ContentFilterField {
  SUBJECT = 'SUBJECT',
  MESSAGE_TEXT = 'MESSAGE_TEXT',
  AUTHOR = 'AUTHOR',
  AUTHOR_EMAIL = 'AUTHOR_EMAIL',
  ANY = 'ANY',
}

export enum ContentFilterLogic {
  INCLUDE = 'INCLUDE',
  EXCLUDE = 'EXCLUDE',
}

export interface TriggerContentFilter {
  type: ContentFilterType;
  value: string;
  field?: ContentFilterField; // default: ANY
  logic?: ContentFilterLogic; // default: INCLUDE
}

export enum ActionType {
  ARCHIVE = 'ARCHIVE',
  ADD_LABELS = 'ADD_LABELS',
  REMOVE_LABELS = 'REMOVE_LABELS',
  FORWARD = 'FORWARD',
  DELETE = 'DELETE',
  MARK_READ = 'MARK_READ',
  MARK_UNREAD = 'MARK_UNREAD',
  NOTIFY_BY_EMAIL = 'NOTIFY_BY_EMAIL',
  CHECK_IF = 'CHECK_IF',
  AI_ASK = 'AI_ASK',
  SUMMARIZE = 'SUMMARIZE',
  DECIDE = 'DECIDE',
  CHANGE_CATEGORY = 'CHANGE_CATEGORY',
  DRAFT_EMAIL = 'DRAFT_EMAIL',
  DRAFT_REPLY = 'DRAFT_REPLY',
  GENERATE_DOBOX = 'GENERATE_DOBOX',
  LOAD_DOBOX = 'LOAD_DOBOX',
  LOAD_COMMUNICATIONS = 'LOAD_COMMUNICATIONS',
  SEND_NOTIFICATION_EMAIL = 'SEND_NOTIFICATION_EMAIL',
  SEND_EMAIL = 'SEND_EMAIL',
  SEND_USER_SLACK_MESSAGE = 'SEND_USER_SLACK_MESSAGE',
  SEND_BOT_SLACK_MESSAGE = 'SEND_BOT_SLACK_MESSAGE',
  VALIDATE_DOBOX_RULES = 'VALIDATE_DOBOX_RULES',
  APPEND_TO_DOBOX = 'APPEND_TO_DOBOX',
  CHECK_AND_APPEND_DOBOX = 'CHECK_AND_APPEND_DOBOX',
  CREATE_DOBOX = 'CREATE_DOBOX',
  RESOLVE_DOBOX = 'RESOLVE_DOBOX',
  ACCEPT_DOBOX_SUGGESTION = 'ACCEPT_DOBOX_SUGGESTION',
  ADD_DOBOX_COMMENT = 'ADD_DOBOX_COMMENT',
  UPDATE_DOBOX_PARTICIPANTS = 'UPDATE_DOBOX_PARTICIPANTS',
  UPDATE_DOBOX_LABELS = 'UPDATE_DOBOX_LABELS',
  /** Platform resolved at runtime from the loaded messages, never from stepConfig (D10). */
  PROCESS_DOBOX = 'PROCESS_DOBOX',
  FOR_EACH = 'FOR_EACH',
  SEND_USER_GOOGLE_CHAT_MESSAGE = 'SEND_USER_GOOGLE_CHAT_MESSAGE',
  MCP_TOOL_CALL = 'MCP_TOOL_CALL',
  CALENDAR_RSVP = 'CALENDAR_RSVP',
  CALENDAR_CREATE_EVENT = 'CALENDAR_CREATE_EVENT',
  CALENDAR_DELETE_EVENT = 'CALENDAR_DELETE_EVENT',
  CALENDAR_RESCHEDULE_EVENT = 'CALENDAR_RESCHEDULE_EVENT',
  CALENDAR_ADD_ATTENDEES = 'CALENDAR_ADD_ATTENDEES',
  CALENDAR_REMOVE_ATTENDEES = 'CALENDAR_REMOVE_ATTENDEES',
  AGENT_TASK = 'AGENT_TASK',
  LOAD_MEETINGS = 'LOAD_MEETINGS',
  SEND_INSTAGRAM_MESSAGE = 'SEND_INSTAGRAM_MESSAGE',
  REPLY_INSTAGRAM_COMMENT = 'REPLY_INSTAGRAM_COMMENT',
  PUBLISH_INSTAGRAM_POST = 'PUBLISH_INSTAGRAM_POST',
  SEND_WHATSAPP_MESSAGE = 'SEND_WHATSAPP_MESSAGE',
  /** Fase 0 — contract only; executor comes in Fase 1. */
  NOTIFY_ASSISTANT = 'NOTIFY_ASSISTANT',
  /** Fase 0 — contract only; executor comes in Fase 1. */
  ASK_USER = 'ASK_USER',
  /** Fase 0 — contract only; executor comes in Fase 1. */
  RUN_ASSISTANT = 'RUN_ASSISTANT',
  /** Loads all teammates of the flow owner, optionally filtered by teamId. */
  LOAD_TEAM_MEMBERS = 'LOAD_TEAM_MEMBERS',
  /** Checks teammates' Google Calendars via the shared teamCalendarService. */
  CHECK_TEAM_CALENDAR = 'CHECK_TEAM_CALENDAR',
  LOAD_KNOWLEDGE_PAGE = 'LOAD_KNOWLEDGE_PAGE',
  APPROVAL_GATE = 'APPROVAL_GATE',
  CLASSIFY_ACTIONABILITY = 'CLASSIFY_ACTIONABILITY',
  SEARCH_PEOPLE = 'SEARCH_PEOPLE',
  UPDATE_PERSON_NOTES = 'UPDATE_PERSON_NOTES',
  LOAD_TEAM_PEOPLE = 'LOAD_TEAM_PEOPLE',
  /** Writes a timeline event on a Person — distinct from QueueMessageType.PERSON_EVENT, which is the inbound "a Person was created" trigger. */
  CREATE_PERSON_EVENT = 'CREATE_PERSON_EVENT',
  GBP_REPLY_REVIEW = 'GBP_REPLY_REVIEW',
  PUBLISH_FACEBOOK_POST = 'PUBLISH_FACEBOOK_POST',
  REPLY_FACEBOOK_COMMENT = 'REPLY_FACEBOOK_COMMENT',
  LOAD_FACEBOOK_POSTS = 'LOAD_FACEBOOK_POSTS',
  LOAD_FACEBOOK_COMMENTS = 'LOAD_FACEBOOK_COMMENTS',
  SEND_FACEBOOK_MESSAGE = 'SEND_FACEBOOK_MESSAGE',
  SEND_TEAMS_MESSAGE = 'SEND_TEAMS_MESSAGE',
  /** Platform-discriminated: SLACK/TEAMS/GOOGLE_CHAT/INSTAGRAM/WHATSAPP/MESSENGER via stepConfig.platform. */
  LOAD_CHAT_COMMUNICATIONS = 'LOAD_CHAT_COMMUNICATIONS',
}

export enum ActionabilityVerdict {
  ACTIONABLE = 'ACTIONABLE',
  NOT_ACTIONABLE = 'NOT_ACTIONABLE',
}

/**
 * Steps that own a `childSteps` branch. They cannot be nested inside one
 * another: persistence only serializes a single level of childSteps, so a
 * container nested in a container would silently drop its own children.
 */
export const CONTAINER_STEP_TYPES: ReadonlySet<ActionType> = new Set([
  ActionType.CHECK_IF,
  ActionType.FOR_EACH,
]);

/**
 * Controls which AgentConversation context a flow→assistant step targets.
 *
 * BACKGROUND_TASKS_SINGLE — Find or create one shared "Background tasks"
 *   conversation per user. Used by NOTIFY_ASSISTANT and ASK_USER (MVP).
 * NEW_EACH_RUN — Spin up a fresh conversation for every flow execution.
 *   Default for RUN_ASSISTANT (MVP).
 * REUSE_SAME — Reuse one shared conversation per (userId, flow) across all
 *   executions. Implemented for RUN_ASSISTANT; still not supported for
 *   NOTIFY_ASSISTANT (notifyAssistantStrategy rejects it).
 */
export enum ConversationMode {
  BACKGROUND_TASKS_SINGLE = 'BACKGROUND_TASKS_SINGLE',
  NEW_EACH_RUN = 'NEW_EACH_RUN',
  REUSE_SAME = 'REUSE_SAME',
}

/** Step config for NOTIFY_ASSISTANT — fire-and-forget message into a system conversation. */
export interface NotifyAssistantStepConfig {
  /** Template string (supports {{variable}} substitution at runtime). */
  messageTemplate: string;
  /** Optional display title for the target conversation. */
  conversationTitle?: string;
  conversationMode: ConversationMode;
}

/** Step config for ASK_USER — pauses flow execution and posts a structured choice card. */
export interface AskUserStepConfig {
  /** Template string for the question shown to the user. */
  messageTemplate: string;
  /**
   * Labelled options the user can click. Must be ≥2 unique non-empty strings.
   * The chosen option is stored as {{step-N.output.decision}} for downstream CHECK_IF steps.
   */
  options: string[];
  /** Optional display title for the target conversation. */
  conversationTitle?: string;
  /** MVP: must be BACKGROUND_TASKS_SINGLE. */
  conversationMode: ConversationMode;
}

/** Step config for APPROVAL_GATE — pauses flow execution and creates an approval DoBox entry. */
export interface ApprovalGateStepConfig {
  /** Template string interpolated against the flow variable context to build the approval summary. */
  summaryPrompt: string;
  /** Optional title shown on the approval DoBox entry. */
  title?: string;
  /** Target DoBox task group; when omitted, the flow owner's default DoBox is used. */
  taskGroupId?: string;
  conversationMode: ConversationMode;
}

export interface ClassifyActionabilityStepConfig {
  inputMode?: ValidateDoBoxRulesInputMode;
  communicationsInput?: string;
  minScore?: number;
  // Replaces minScore for an email whose target user is among a few "to"
  // addressees, so the step after the gate decides who owns its ask.
  smallAudienceMinScore?: number;
}

/** Step config for RUN_ASSISTANT — invokes the planner lambda and runs an agent loop. */
export interface RunAssistantStepConfig {
  /** Prompt template sent to the planner as the initial user message. */
  promptTemplate: string;
  /** Optional display title for the spawned conversation. */
  conversationTitle?: string;
  /**
   * Tool names pre-approved for auto-execution inside the spawned agent run.
   * Validation against the tool registry is deferred to Fase 2.
   */
  autoApprovedTools?: string[];
  /**
   * When true, the spawned agent run has no human watching. propose_actions will
   * not surface pending proposal cards for unapproved tools — instead it returns a
   * structured blocked result so the model finishes cleanly rather than hanging.
   * Default / undefined = interactive.
   */
  nonInteractive?: boolean;
  /** When true, the spawned conversation is archived when the run reaches a terminal state (COMPLETED or STOPPED). */
  closeOnComplete?: boolean;
  /** NEW_EACH_RUN or REUSE_SAME. */
  conversationMode: ConversationMode;
}

const TRIGGERS_REQUIRING_ELEVATED_ROLE: ReadonlySet<TriggerKey> = new Set([
  TriggerKey.INSTAGRAM_NEW_MESSAGE,
  TriggerKey.INSTAGRAM_POST_COMMENT,
]);

/**
 * Triggers where elevated role is required only when the trigger targets a
 * TEAM-owned resource. When ownerType is USER, the trigger is self-scoped and
 * no elevated role is needed.
 */
const TRIGGERS_REQUIRING_ELEVATED_ROLE_FOR_TEAM: ReadonlySet<TriggerKey> =
  new Set([TriggerKey.KNOWLEDGE_PAGE_CREATED]);

/**
 * Returns true when the given trigger key may only be created by a team
 * OWNER or ADMIN. Centralizes the team-scoped trigger policy so adding a
 * new team-scoped platform (Facebook, …) is a one-line change. WhatsApp is
 * NOT team-scoped — its UserCommunications are owned by the individual who
 * connected the number (HookListener passes credentials.userId as appUserId).
 *
 * For triggers in TRIGGERS_REQUIRING_ELEVATED_ROLE_FOR_TEAM, elevation is
 * required only when config.ownerType is TEAM. If the key is in that set
 * and config is omitted, the function FAILS SECURE — returns true — so a
 * caller that forgets to pass config can never silently bypass the role
 * gate. The two known arity-1 call sites today (validateInstagramNewMessageTrigger,
 * and tests) only ask about INSTAGRAM_NEW_MESSAGE which is in the unconditional
 * set, so this fail-secure path is unreachable for them; it is a guard for
 * future trigger additions.
 */
export const requiresElevatedRoleForTrigger = (
  key: TriggerKey,
  config?: TriggerConfig,
): boolean => {
  if (TRIGGERS_REQUIRING_ELEVATED_ROLE.has(key)) return true;
  if (TRIGGERS_REQUIRING_ELEVATED_ROLE_FOR_TEAM.has(key)) {
    if (!config) return true;
    return (
      (config as KnowledgePageTriggerConfig).ownerType ===
      KnowledgeOwnerType.TEAM
    );
  }
  return false;
};

export const CRON_TRIGGER_KEYS = [TriggerKey.CRON_SCHEDULE];

export const MANUAL_TRIGGER_KEYS = [TriggerKey.MANUAL_RUN];

export const PEOPLE_TRIGGER_KEYS = [
  TriggerKey.PERSON_CREATED,
  TriggerKey.LANDING_PAGE_LEAD_CREATED,
];

export type ConditionLogic = 'AND' | 'OR';

export interface CheckIfCondition {
  variable: string;
  operator: string;
  value: string;
}

export interface CheckIfStepConfig {
  conditions: CheckIfCondition[];
  conditionLogic?: ConditionLogic;
}

export interface PromptStepConfig {
  prompt: string;
  outputSchema?: string;
}

/**
 * Hard ceiling for both FOR_EACH `maxIterations` and LOAD_DOBOX `limit`, and
 * the default iteration count when `maxIterations` is unset.
 *
 * FOR_EACH runs its child steps sequentially inside a single 180s Lambda
 * invocation. An unbounded count (e.g. an AGENT_TASK per item) would time out
 * mid-run and risk duplicate side effects on SQS retry. The editor clamps to
 * this value (and, for FOR_EACH, to the source step's load limit) and the
 * backend re-clamps defensively — never trust the client config alone.
 */
export const MAX_FLOW_ITEM_LIMIT = 50;

export interface ForEachStepConfig {
  sourceArray: string;
  maxIterations?: string;
}

export interface McpToolCallStepConfig {
  connectionId: string;
  toolName: string;
  arguments: Record<string, unknown>;
}

export enum AgentToolSet {
  EMAIL_ACTIONS = 'EMAIL_ACTIONS',
  COMMUNICATIONS = 'COMMUNICATIONS',
  NOTIFICATIONS = 'NOTIFICATIONS',
  DOBOX = 'DOBOX',
  MCP = 'MCP',
  MEMORY = 'MEMORY',
  CALENDAR = 'CALENDAR',
  LOAD_MEETINGS = 'LOAD_MEETINGS',
  CONVERSATIONAL = 'CONVERSATIONAL',
  INSTAGRAM = 'INSTAGRAM',
  WHATSAPP = 'WHATSAPP',
  FACEBOOK = 'FACEBOOK',
  KNOWLEDGE = 'KNOWLEDGE',
  KNOWLEDGE_WRITE = 'KNOWLEDGE_WRITE',
  ARTIFACT = 'ARTIFACT',
  PEOPLE = 'PEOPLE',
  TEAMS = 'TEAMS',
}

const CALENDAR_TOOL_NAMES = [
  'load_meetings',
  'check_meetings_with',
  'create_calendar_event',
  'delete_calendar_event',
  'reschedule_calendar_event',
  'rsvp_calendar_event',
  'add_event_attendees',
  'remove_event_attendees',
] as const;

const KNOWLEDGE_READ_TOOL_NAMES = [
  'list_knowledge_spaces',
  'list_knowledge_pages',
  'list_knowledge_tree',
  'search_knowledge',
  'get_knowledge_page',
] as const;

const KNOWLEDGE_WRITE_ONLY_TOOL_NAMES = [
  'execute_create_knowledge_space',
  'execute_create_knowledge_page',
  'execute_edit_knowledge_page',
  'execute_rename_knowledge_page',
  'execute_delete_knowledge_page',
] as const;

const KNOWLEDGE_WRITE_TOOL_NAMES = [
  ...KNOWLEDGE_READ_TOOL_NAMES,
  ...KNOWLEDGE_WRITE_ONLY_TOOL_NAMES,
] as const;

export const ARTIFACT_TOOL_NAME = 'build_artifact' as const;
export const EXPORT_ARTIFACT_PDF_TOOL_NAME = 'export_artifact_pdf' as const;

/**
 * Authoritative map of which agent tool names each toolset exposes. Single
 * source of truth for documentation (e.g. the agent-builder system prompt).
 * A guard test cross-checks this against the runtime TOOL_SET_BUILDERS so the
 * two cannot drift. MCP is dynamic (tools are loaded per connection at runtime)
 * and therefore has no static list.
 */
export const AGENT_TOOLSET_TOOLS: Record<AgentToolSet, readonly string[]> = {
  [AgentToolSet.EMAIL_ACTIONS]: [
    'archive_email',
    'mark_email_read',
    'mark_email_unread',
    'add_labels',
    'remove_labels',
    'change_email_category',
    'load_email_labels',
    'create_email_label',
    'draft_reply',
    'send_reply',
    'draft_email',
    'send_new_email',
    'unsubscribe_from_email',
    'get_message_content',
  ],
  [AgentToolSet.COMMUNICATIONS]: [
    'load_emails',
    'load_slack_messages',
    'load_google_chat_messages',
    'get_message_content',
  ],
  [AgentToolSet.NOTIFICATIONS]: [
    'send_notification_email',
    'send_user_slack_message',
    'send_bot_slack_message',
    'list_slack_workspaces',
    'list_slack_channels_and_members',
    'send_user_google_chat_message',
    'list_google_chat_accounts',
    'list_google_chat_spaces_and_members',
  ],
  [AgentToolSet.DOBOX]: [
    'load_dobox',
    'create_dobox',
    'resolve_dobox',
    'accept_dobox_suggestion',
    'add_dobox_comment',
    'get_dobox_comments',
    'update_dobox_participants',
    'update_dobox_labels',
    'append_to_dobox',
    'generate_dobox',
  ],
  [AgentToolSet.MCP]: [],
  [AgentToolSet.MEMORY]: [
    'recall_memory',
    'save_memory',
    'get_past_executions',
  ],
  [AgentToolSet.CALENDAR]: CALENDAR_TOOL_NAMES,
  [AgentToolSet.LOAD_MEETINGS]: CALENDAR_TOOL_NAMES,
  [AgentToolSet.CONVERSATIONAL]: ['ask_slack_question', 'wait_for_replies'],
  [AgentToolSet.INSTAGRAM]: [
    'load_instagram_integrations',
    'load_instagram_conversations',
    'load_instagram_communications',
    'send_instagram_message',
    'instagram_list_media',
    'instagram_list_comments',
    'instagram_insights',
    'instagram_reply_comment',
  ],
  [AgentToolSet.WHATSAPP]: [
    'load_whatsapp_numbers',
    'load_whatsapp_communications',
    'send_whatsapp_message',
  ],
  [AgentToolSet.FACEBOOK]: ['facebook_list_posts', 'facebook_list_comments'],
  [AgentToolSet.KNOWLEDGE]: KNOWLEDGE_READ_TOOL_NAMES,
  [AgentToolSet.KNOWLEDGE_WRITE]: KNOWLEDGE_WRITE_TOOL_NAMES,
  [AgentToolSet.ARTIFACT]: [ARTIFACT_TOOL_NAME, EXPORT_ARTIFACT_PDF_TOOL_NAME],
  [AgentToolSet.PEOPLE]: [
    'search_people',
    'get_person',
    'create_person',
    'update_person_notes',
    'update_person_emails',
    'update_person_phones',
    'update_person_job_title',
    'delete_person',
    'list_team_people',
    'create_person_event',
    'add_person_to_team',
  ],
  [AgentToolSet.TEAMS]: ['list_teams_chats', 'send_teams_message'],
};

export enum AgentModelTier {
  STANDARD = 'standard',
  FAST = 'fast',
}

export interface AgentTaskStepConfig {
  goal: string;
  /** Optional persona/role system prompt, prepended to the generated execution
   * rules. Lets the user keep the agent's identity out of the goal text. */
  systemPrompt?: string;
  toolSets: AgentToolSet[];
  maxTurns?: number;
  modelTier?: AgentModelTier;
  /** When MCP toolset is enabled, limit to these connection IDs. Empty/undefined = all connections. */
  mcpConnectionIds?: string[];
  /** IDs of AgentTemplates to use as specialist sub-agents. */
  agentTemplateIds?: string[];
  // When set, the step runs this saved AgentTemplate directly; goal is optional.
  primaryAgentTemplateId?: string;
  /** JSON schema string forcing structured output after the agent run. Same shape as AiAskStepConfig.outputSchema. */
  outputSchema?: string;
}

export type StepConfig =
  | LabelStepConfig
  | ChangeCategoryStepConfig
  | RecipientsStepConfig
  | PromptStepConfig
  | DraftEmailStepConfig
  | DraftReplyStepConfig
  | CheckIfStepConfig
  | GenerateDoBoxStepConfig
  | LoadDoBoxStepConfig
  | LoadCommunicationsStepConfig
  | SendNotificationEmailStepConfig
  | SendEmailStepConfig
  | SendSlackMessageStepConfig
  | ValidateDoBoxRulesStepConfig
  | AppendToDoBoxStepConfig
  | CheckAndAppendDoBoxStepConfig
  | CreateDoBoxStepConfig
  | ResolveDoBoxStepConfig
  | AcceptDoBoxSuggestionStepConfig
  | AddDoBoxCommentStepConfig
  | UpdateDoBoxParticipantsStepConfig
  | UpdateDoBoxLabelsStepConfig
  | ForEachStepConfig
  | SendGoogleChatMessageStepConfig
  | ProcessDoBoxStepConfig
  | McpToolCallStepConfig
  | CalendarRsvpStepConfig
  | CalendarCreateEventStepConfig
  | CalendarDeleteEventStepConfig
  | CalendarRescheduleEventStepConfig
  | CalendarAddAttendeesStepConfig
  | CalendarRemoveAttendeesStepConfig
  | AgentTaskStepConfig
  | LoadMeetingsStepConfig
  | SendInstagramMessageStepConfig
  | ReplyInstagramCommentStepConfig
  | PublishInstagramPostStepConfig
  | SendWhatsAppMessageStepConfig
  | NotifyAssistantStepConfig
  | AskUserStepConfig
  | ApprovalGateStepConfig
  | RunAssistantStepConfig
  | LoadTeamMembersStepConfig
  | CheckTeamCalendarStepConfig
  | LoadKnowledgePageStepConfig
  | ClassifyActionabilityStepConfig
  | SearchPeopleStepConfig
  | UpdatePersonNotesStepConfig
  | LoadTeamPeopleStepConfig
  | CreatePersonEventStepConfig
  | GbpReplyReviewStepConfig
  | FacebookPublishPostStepConfig
  | FacebookReplyCommentStepConfig
  | LoadFacebookPostsStepConfig
  | LoadFacebookCommentsStepConfig
  | SendFacebookMessageStepConfig
  | SendTeamsMessageStepConfig
  | LoadChatCommunicationsStepConfig;

export type TriggerConfig =
  | AllEmailsTriggerConfig
  | PrimaryInboxTriggerConfig
  | LabelMatchTriggerConfig
  | CategoryMatchTriggerConfig
  | EmailMatchTriggerConfig
  | DomainMatchTriggerConfig
  | SlackNewMessageTriggerConfig
  | SlackChannelMatchTriggerConfig
  | SlackAuthorMatchTriggerConfig
  | GoogleChatNewMessageTriggerConfig
  | GoogleChatSpaceMatchTriggerConfig
  | GoogleChatAuthorMatchTriggerConfig
  | InstagramNewMessageTriggerConfig
  | InstagramPostCommentTriggerConfig
  | WhatsAppNewMessageTriggerConfig
  | BaseDoboxTriggerConfig
  | CommentAddedConfig
  | KnowledgePageTriggerConfig
  | GitHubEventMatchTriggerConfig
  | FacebookNewCommentTriggerConfig
  | FacebookNewMessageTriggerConfig
  | PersonCreatedTriggerConfig
  | TeamsNewMessageTriggerConfig;

export const MAX_HOURS_BACK = 168;

export interface UserCommunicationAgentBase {
  agentId: string;
  entityId: string;
  userId: string;
  teamId?: string;
  entityType: string;
  isActive: boolean;
  isSystem?: boolean;
  createdAt: number;
  updatedAt: number;
}

export interface UserCommunicationAgentMetadata
  extends UserCommunicationAgentBase {
  name: string;
  groupId?: string;
  createdFromGroupId?: string;
  createdFromTemplateId?: string;
  isSetupOnly?: boolean;
}

export interface UserCommunicationAgentTrigger
  extends UserCommunicationAgentBase {
  triggerType: TriggerType;
  triggerKey: TriggerKey;
  triggerConfig?: TriggerConfig;
  contentFilter?: TriggerContentFilter;
  cronExpression?: string;
  cronExpressionTimezone?: string;
  startDate?: string;
  endDate?: string;
}

export type UserCommunicationAgentChildStep = Omit<
  UserCommunicationAgentStep,
  keyof UserCommunicationAgentBase | 'childSteps'
>;

export interface UserCommunicationAgentStep extends UserCommunicationAgentBase {
  stepType: ActionType;
  order: number;
  stepName?: string;
  stepConfig?: StepConfig;
  childSteps?: UserCommunicationAgentChildStep[];
}

export type UserCommunicationAgentItem =
  | UserCommunicationAgentMetadata
  | UserCommunicationAgentTrigger
  | UserCommunicationAgentStep;

export interface UserCommunicationAgent extends UserCommunicationAgentMetadata {
  trigger?: UserCommunicationAgentTrigger;
  steps?: UserCommunicationAgentStep[];
}

export interface PaginatedUserCommunicationAgentsResponse {
  userCommunicationAgents: UserCommunicationAgent[];
  exclusiveStartKey: string | null;
}

export enum QueueMessageType {
  COMMUNICATION = 'COMMUNICATION',
  SCHEDULED = 'SCHEDULED',
  PENDING_SCHEDULE = 'PENDING_SCHEDULE',
  DOBOX_EVENT = 'DOBOX_EVENT',
  CONVERSATION_REPLY = 'CONVERSATION_REPLY',
  MANUAL = 'MANUAL',
  /** Fase 3 — resumes a paused FlowExecution from its saved cursor. */
  CONTINUE_FLOW_EXECUTION = 'CONTINUE_FLOW_EXECUTION',
  KNOWLEDGE_PAGE_EVENT = 'KNOWLEDGE_PAGE_EVENT',
  AUTO_RESOLVE_SUGGESTIONS = 'AUTO_RESOLVE_SUGGESTIONS',
  PERSON_EVENT = 'PERSON_EVENT',
  /** Generates/refreshes a cached DoBox plan for one entry (see prepareDoBoxPlan.ts). */
  PREPARE_DOBOX_PLAN = 'PREPARE_DOBOX_PLAN',
  /** Arms a delayed check of whether an executor approval ledger row is about to expire. */
  ARM_EXECUTOR_EXPIRY_WARNING = 'ARM_EXECUTOR_EXPIRY_WARNING',
  /** Fires the armed check — posts the expiry warning if the ledger row is still PENDING. */
  FIRE_EXECUTOR_EXPIRY_WARNING = 'FIRE_EXECUTOR_EXPIRY_WARNING',
}

export interface UserCommunicationAgentQueueBodyBase {
  messageType: QueueMessageType;
  userId: string;
  /**
   * Set by `processRecord.ts` when a flow is deferred because the user has a
   * setup execution in flight (see `FlowExecutionStore.getInFlightSetupExecutionForUser`).
   * The redelivered message carries this incremented so the handler can bound
   * how many times a single flow gets deferred — once it hits
   * `MAX_SETUP_EXECUTION_DEFERRALS` the flow runs regardless, so a stalled
   * setup run can delay but never permanently block another flow.
   */
  flowDeferralCount?: number;
}

export interface CommunicationQueueBody
  extends UserCommunicationAgentQueueBodyBase {
  messageType: QueueMessageType.COMMUNICATION;
  messageId: string;
  /**
   * Owner of the referenced UserCommunication record. Diverges from `userId`
   * for team-scoped platforms (e.g. Instagram fan-out) where `userId` in the
   * queue body identifies the agent owner (an admin/owner of the team) and
   * `communicationOwnerUserId` identifies the team whose communication fired
   * the flow.
   *
   * Invariant: `userId` ALWAYS identifies the agent owner. Consumers that
   * need to fetch the UserCommunication row MUST read
   * `communicationOwnerUserId ?? userId`; consumers that fetch the agent
   * itself (or its triggers) use `userId` directly.
   */
  communicationOwnerUserId?: string;
  /**
   * When true, the resolution step bypasses the `contactsOnly` trigger filter.
   * Set ONLY by the user-initiated scan (`dispatchAgentsForScan`), never by live
   * dispatch: contacts are ingested asynchronously, so a scan run soon after a
   * mailbox is connected fails the contact lookup for most senders — and the
   * scan marks each message seen for a week either way, leaving them
   * unrecoverable. The trade is deliberate: a scan therefore also surfaces mail
   * from senders who are not contacts, which live watching would filter out.
   */
  ignoreContactsOnly?: boolean;
  /**
   * Set by the UserCommunications stream on an inbound reply to a thread that
   * already has a DoBox. Only system agents resolve for it: their PROCESS_DOBOX
   * reads the thread's DoBoxes and decides, while a user flow that creates a
   * DoBox would add one per reply.
   */
  threadHasDoBox?: boolean;
}

export interface AutoResolveSuggestionsQueueBody
  extends UserCommunicationAgentQueueBodyBase {
  messageType: QueueMessageType.AUTO_RESOLVE_SUGGESTIONS;
  messageId: string;
  /**
   * Owner of the referenced UserCommunication record. Diverges from `userId`
   * for team-scoped platforms (e.g. Instagram fan-out) where `userId` in the
   * queue body identifies the agent owner and `communicationOwnerUserId`
   * identifies the team whose communication fired the resolution pass.
   *
   * Consumers that need to fetch the UserCommunication row MUST read
   * `communicationOwnerUserId ?? userId`.
   */
  communicationOwnerUserId?: string;
  /**
   * Set by the open follow-up link: the DoBox this message was just linked to, judged
   * alone. The message's own pass already judged its conversation's DoBoxes.
   */
  linkedEntryId?: string;
}

export interface ScheduledQueueBody
  extends UserCommunicationAgentQueueBodyBase {
  messageType: QueueMessageType.SCHEDULED;
  agentId: string;
}

interface PendingScheduleQueueBodyBase
  extends UserCommunicationAgentQueueBodyBase {
  messageType: QueueMessageType.PENDING_SCHEDULE;
  agentId: string;
  /**
   * The message that closed the burst. The debounce state records the
   * latest message id on every message and each fire's `Target.Input` carries it.
   * Optional: schedules armed before this field existed fire without it, and a
   * message deleted inside the window no longer resolves — both degrade to a
   * seed carrying the channel identifiers alone.
   */
  messageId?: string;
  /**
   * Owner of the referenced UserCommunication record. Diverges from `userId`
   * for team-scoped platforms (e.g. Instagram fan-out) where `userId` in the
   * queue body identifies the agent owner and `communicationOwnerUserId`
   * identifies the team whose communication armed the schedule.
   *
   * Consumers that need to fetch the UserCommunication row MUST read
   * `communicationOwnerUserId ?? userId`.
   */
  communicationOwnerUserId?: string;
  scheduledFor?: number;
}

export interface SlackPendingScheduleBody extends PendingScheduleQueueBodyBase {
  platform: MessagingPlatform.SLACK;
  /** Slack channel ID, e.g. C012345 */
  channelId: string;
  /** Slack workspace/team ID */
  workspaceId: string;
}

export interface InstagramPendingScheduleBody
  extends PendingScheduleQueueBodyBase {
  platform: MessagingPlatform.INSTAGRAM;
  /** Instagram conversation ID */
  conversationId: string;
  /** Instagram Business Account ID */
  igAccountId: string;
  /** Story reply: id of the replied-to story. Present only when the debounced message was a story reply. */
  storyId?: string;
  /** Story reply: signed CDN url of the replied-to story. PERISHABLE — expires within hours. */
  storyUrl?: string;
}

export interface GoogleChatPendingScheduleBody
  extends PendingScheduleQueueBodyBase {
  platform: MessagingPlatform.GOOGLE_CHAT;
  /** Google Chat space resource name, e.g. spaces/AAAA… */
  spaceName: string;
  /** Subscriber Gmail address used for credential resolution */
  emailAddress: string;
  /** DIRECT_MESSAGE | SPACE | GROUP_CHAT */
  spaceType: GoogleChatSpaceType;
}

export interface WhatsAppPendingScheduleBody
  extends PendingScheduleQueueBodyBase {
  platform: MessagingPlatform.WHATSAPP;
  /** WhatsAppMetaCredentials.phoneNumberId — the business number that received */
  phoneNumberId: string;
  /** `UserCommunication.authorUserId` — a BSUID whenever Meta sends one. */
  counterpartId: string;
  senderName?: string;
  senderUsername?: string;
  displayPhoneNumber?: string;
}

export interface TeamsPendingScheduleBody extends PendingScheduleQueueBodyBase {
  platform: MessagingPlatform.TEAMS;
  /** Graph chat id, e.g. 19:meeting_...@thread.v2 */
  chatId: string;
  /** {oid}.{tid} of the account that received the chat. Not part of the schedule key. */
  homeAccountId: string;
  /** Already the derived display name — no ROW_WINS_OVER_BODY entry needed. */
  chatName?: string;
  chatType?: TeamsChatType;
}

export interface FacebookPendingScheduleBody
  extends PendingScheduleQueueBodyBase {
  platform: MessagingPlatform.MESSENGER;
  /** Connected Facebook Page id */
  pageId: string;
  /** Messenger conversation id, or the sender PSID when Graph's lookup fell back */
  conversationId: string;
  pageName?: string;
}

export type PendingScheduleQueueBody =
  | SlackPendingScheduleBody
  | InstagramPendingScheduleBody
  | GoogleChatPendingScheduleBody
  | WhatsAppPendingScheduleBody
  | TeamsPendingScheduleBody
  | FacebookPendingScheduleBody;

export interface ConversationReplyQueueBody
  extends UserCommunicationAgentQueueBodyBase {
  messageType: QueueMessageType.CONVERSATION_REPLY;
  executionId: string;
  agentId: string;
  timestampId: string;
  pendingQuestionId: string;
}

export interface ManualQueueBody extends UserCommunicationAgentQueueBodyBase {
  messageType: QueueMessageType.MANUAL;
  agentId: string;
  /** Test runs: seed the flow with this stored communication as the trigger event. */
  sampleCommunicationId?: string;
  /**
   * Scenario-simulation test runs: seed the flow with this transient,
   * never-persisted synthetic communication as the trigger event. The
   * resolver has already confirmed it would fire the trigger before enqueueing.
   */
  syntheticCommunication?: SyntheticCommunicationInput;
  /**
   * Set by `triggerInitialSetupExecution` for the one-time seed run of an
   * `isSetupOnly` agent. The runner passes this through to
   * `FlowExecutionStore.createExecution` so the resulting row is stamped
   * `isSetupExecution: true` — never set by any other MANUAL enqueue path
   * (e.g. `testRunAgent`, which `assertAgentIsManuallyExecutable` already
   * blocks for `isSetupOnly` agents).
   */
  isSetupExecution?: boolean;
}

/** Fase 3 — payload that resumes a paused FlowExecution from its saved cursor. */
export interface ContinueFlowExecutionQueueBody
  extends UserCommunicationAgentQueueBodyBase {
  messageType: QueueMessageType.CONTINUE_FLOW_EXECUTION;
  executionId: string;
}

export interface KnowledgePageEventQueueBody
  extends UserCommunicationAgentQueueBodyBase {
  messageType: QueueMessageType.KNOWLEDGE_PAGE_EVENT;
  triggerKey: TriggerKey.KNOWLEDGE_PAGE_CREATED;
  pageId: string;
  spaceId: string;
  ownerType: KnowledgeOwnerType;
  ownerId: string;
  version: number;
  s3Key: string;
}

/**
 * Generates/refreshes a cached DoBox plan for one entry. `userId` (inherited
 * from the base) is the entry's `createdFor`: the plan is built in the
 * owner's executor context, never the invoking admin's.
 */
export interface PrepareDoBoxPlanQueueBody
  extends UserCommunicationAgentQueueBodyBase {
  messageType: QueueMessageType.PREPARE_DOBOX_PLAN;
  taskGroupId: string;
  inboxEntryId: string;
  trigger: PrepareTrigger;
  /** Message this plan should anchor a later reply/undo to, when known. */
  anchorMessageId?: string;
  /** Content fingerprint this PREPARE run was seeded from — guards a stale
   *  re-run from clobbering a newer plan. */
  fingerprint: string;
}

interface ExecutorExpiryWarningQueueBodyBase
  extends UserCommunicationAgentQueueBodyBase {
  conversationId: string;
  runId: string;
  msgIdx: string;
  taskGroupId: string;
  entryId: string;
  /** Epoch ms the approval ledger row this warning watches expires. */
  expiresAt: number;
}

/** Arms a delayed check of whether an executor approval ledger row is about to expire. */
export interface ArmExecutorExpiryWarningQueueBody
  extends ExecutorExpiryWarningQueueBodyBase {
  messageType: QueueMessageType.ARM_EXECUTOR_EXPIRY_WARNING;
}

/** Fires the armed check: posts the expiry warning if the ledger row is still PENDING. */
export interface FireExecutorExpiryWarningQueueBody
  extends ExecutorExpiryWarningQueueBodyBase {
  messageType: QueueMessageType.FIRE_EXECUTOR_EXPIRY_WARNING;
}

export type UserCommunicationAgentQueueBody =
  | CommunicationQueueBody
  | ScheduledQueueBody
  | PendingScheduleQueueBody
  | DoBoxEventQueueBody
  | ConversationReplyQueueBody
  | ManualQueueBody
  | ContinueFlowExecutionQueueBody
  | KnowledgePageEventQueueBody
  | AutoResolveSuggestionsQueueBody
  | PersonEventQueueBody
  | PrepareDoBoxPlanQueueBody
  | ArmExecutorExpiryWarningQueueBody
  | FireExecutorExpiryWarningQueueBody;
